import React, { createContext, useContext, useState, ReactNode } from 'react';

interface PageMetadata {
  title: string;
  description?: string;
  backUrl?: string; // Custom back button destination
  logoUrl?: string; // Optional logo to display in header (e.g., workspace logo)
  helpContentKey?: string; // Contextual help entry rendered as a HelpTrigger beside the title
}

interface PageMetadataContextType {
  pageMetadata: PageMetadata | null;
  setPageMetadata: (metadata: PageMetadata | null) => void;
}

const PageMetadataContext = createContext<PageMetadataContextType | undefined>(undefined);

export const PageMetadataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [pageMetadata, setPageMetadata] = useState<PageMetadata | null>(null);

  return (
    <PageMetadataContext.Provider value={{ pageMetadata, setPageMetadata }}>
      {children}
    </PageMetadataContext.Provider>
  );
};

/**
 * Outside a provider there is no global header to write into, so the setter is
 * a no-op. Kept at module scope so its identity is stable across renders —
 * usePageTitle lists setPageMetadata as an effect dependency.
 */
const NO_PROVIDER_CONTEXT: PageMetadataContextType = {
  pageMetadata: null,
  setPageMetadata: () => {},
};

/**
 * Read/write the authenticated Layout's header metadata.
 *
 * Degrades to a no-op outside PageMetadataProvider instead of throwing. Only
 * the authenticated Layout route mounts the provider, but page components are
 * reused on public, standalone and Pro-tab routes (public person/ballot pages,
 * respond/unsubscribe links). The header title is cosmetic chrome; throwing
 * turned every such reuse into a whole-page crash (Sentry FRONTEND-27, #1452)
 * and had already forced several per-page workarounds.
 */
export const usePageMetadata = (): PageMetadataContextType => {
  const context = useContext(PageMetadataContext);
  return context ?? NO_PROVIDER_CONTEXT;
};
